import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('index')

import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalizedLoaded } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getConfig } from '@/config'
import { shouldResumeOnboarding } from '@/config/onboarding'
import { BUILD_FEATURES } from '@/config/build-features'
import { trackPageView } from '@/lib/analytics'
import { LEGACY_CMS_PAGE_REDIRECTS } from '@/lib/cms/shortcodeLinks'

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/library/:tab?',
    name: 'library',
    component: () => import('@/views/Library.vue'),
    props: (route) => ({ tab: route.params.tab || 'played' }),
    meta: { requiresAuth: true }
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('@/views/Search.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { requiresAuth: false }
  },
  // TV device login approval page (verification_uri for the RFC 8628 device flow)
  {
    path: '/activate',
    name: 'activate',
    component: () => import('@/views/auth/Activate.vue'),
    props: (route) => ({ code: route.query.code as string }),
    meta: { requiresAuth: true }
  },
  {
    path: '/subscribe',
    name: 'subscribe',
    component: () => import('@/views/Subscribe.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/settings/:section?',
    name: 'settings',
    component: () => import('@/views/Settings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/Support.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/support/contact',
    name: 'support-contact',
    component: () => import('@/views/SupportContact.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/CmsPage.vue'),
    props: { cmsSlug: 'terms-of-use' },
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/CmsPage.vue'),
    props: { cmsSlug: 'privacy-policy' },
    meta: { requiresAuth: false }
  },
  ...Object.entries(LEGACY_CMS_PAGE_REDIRECTS).map(([slug, redirect]) => ({
    path: `/pages/${slug}`,
    redirect,
  })),
  {
    path: '/pages/onboarding',
    redirect: '/',
  },
  {
    path: '/pages/:slug',
    name: 'cms-page',
    component: () => import('@/views/CmsPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/test/streaming',
    name: 'streaming-test',
    component: () => import('@/components/test/StreamingTest.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/Onboarding.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-reset-code',
    name: 'verify-reset-code',
    component: () => import('@/views/auth/VerifyResetCode.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false },
    props: (route) => ({ 
      token: route.query.token as string, 
      email: route.query.email as string 
    })
  },
  {
    path: '/reset-password',
    name: 'reset-password-query',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false },
    props: (route) => ({ 
      token: route.query.token as string, 
      email: route.query.email as string 
    })
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false },
    props: true
  },
  // Legacy route - keep for backward compatibility
  {
    path: '/browse/:resource',
    name: 'browse',
    component: () => import('@/views/Browse.vue'),
    meta: { requiresAuth: false }
  },
  // Enhanced routes with slugs for better SEO
  {
    path: '/browse/authors/:slug',
    name: 'author-detail',
    component: () => import('@/views/Browse.vue'),
    props: (route: RouteLocationNormalizedLoaded) => ({ 
      resource: 'authors',
      slug: route.params.slug
    }),
    meta: { requiresAuth: false }
  },
  {
    path: '/browse/categories/:slug',
    name: 'category-detail',
    component: () => import('@/views/Browse.vue'),
    props: (route: RouteLocationNormalizedLoaded) => ({ 
      resource: 'categories',
      slug: route.params.slug
    }),
    meta: { requiresAuth: false }
  },
  {
    path: '/browse/series/:slug',
    name: 'series-detail',
    component: () => import('@/views/Browse.vue'),
    props: (route: RouteLocationNormalizedLoaded) => ({ 
      resource: 'series',
      slug: route.params.slug 
    }),
    meta: { requiresAuth: false }
  },
  {
    path: '/browse/casts/:slug',
    name: 'cast-detail',
    component: () => import('@/views/Browse.vue'),
    props: (route: RouteLocationNormalizedLoaded) => ({ 
      resource: 'casts',
      slug: route.params.slug 
    }),
    meta: { requiresAuth: false }
  },
  // Title detail routes - support both with and without slug
  {
    path: '/titles/:id/:slug?',
    name: 'title',
    component: () => import('@/views/Detail.vue'),
    props: true,
    meta: { requiresAuth: false }
  },
  // EPUB Reader route - only include if epubReader feature is enabled
  ...(BUILD_FEATURES.epubReader ? [{
    path: '/reader/:id/:slug?',
    name: 'epub-reader',
    component: () => import('@/views/EpubReader.vue'),
    props: true,
    meta: { requiresAuth: false }
  }] : []),
  ...(BUILD_FEATURES.pdfReader ? [{
    path: '/pdf/:id/:slug?',
    name: 'pdf-reader',
    component: () => import('@/views/PdfReader.vue'),
    props: true,
    meta: { requiresAuth: true }
  }] : []),
  // Finished page route
  {
    path: '/finished/:titleId?',
    name: 'finished',
    component: () => import('@/views/Finished.vue'),
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/continue-listening/:titleId',
    name: 'continue-listening',
    component: () => import('@/views/ContinueListening.vue'),
    props: true,
    meta: { requiresAuth: false }
  },
  {
    path: '/continue-reading/:titleId',
    name: 'continue-reading',
    component: () => import('@/views/ContinueListening.vue'),
    props: true,
    meta: { requiresAuth: false }
  },
  // Purchase page route - special checkout without login
  {
    path: '/purchase/:titleUuid/:userIdHash',
    name: 'purchase',
    component: () => import('@/views/Purchase.vue'),
    props: true,
    meta: { requiresAuth: false }
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (authStore.isAuthenticated && shouldResumeOnboarding(to.name, getConfig().app.id)) {
    next({ name: 'onboarding' })
    return
  }

  // For routes that don't require auth, just proceed immediately
  if (!to.meta.requiresAuth) {
    next()
    return
  }
  
  // For routes requiring auth, wait for initialization but with a timeout
  const initializationPromise = authStore.waitForInitialization()
  
  // Create a timeout promise to avoid blocking too long
  const timeoutPromise = new Promise<void>(resolve => {
    setTimeout(() => resolve(), 1000) // 1 second timeout
  })
  
  // Wait for initialization or timeout, whichever comes first
  await Promise.race([initializationPromise, timeoutPromise])
  
  // Check authentication after initialization or timeout
  if (!authStore.isAuthenticated) {
    fileLogger.log(`[Router] Unauthenticated user attempting to access protected route: ${to.path}`)
    
    // Store the intended destination in localStorage instead of query parameter
    // This reduces the chance of URL-based redirect chains
    localStorage.setItem('redirectAfterLogin', to.fullPath)
    
    // Redirect to login page
    next({ 
      name: 'login'
    })
  } else {
    // User is authenticated, proceed normally
    next()
  }
})

// After navigation guard to preserve video state
router.afterEach((to, from) => {
  trackPageView(to.fullPath, typeof to.meta?.title === 'string' ? to.meta.title : undefined)

  if (!BUILD_FEATURES.video) {
    return
  }
  
  // Import VideoService dynamically to avoid circular dependencies
  import('@/services/video.service').then(({ VideoService }) => {
    const videoService = VideoService.getInstance()
    
    // If video service is initialized and playing, preserve its state
    if (videoService.isInitialized() && videoService.isPlaying()) {
      fileLogger.log(`[Router] Preserving video state during navigation from ${from.path} to ${to.path}`)
      
      // The video service will handle moving the video element to the new container
      // when the VideoPlayer component mounts
    }
  }).catch((error) => {
    fileLogger.error('Failed to import VideoService in router guard:', error)
  })
})

export default router
